// Confirm-with-reason modal. Generic primitive used by audited operator
// actions (contact lifecycle transition, L4 summary override, deal-stage
// moves, campaign status transitions, AI-context / KPI target inline
// edits). Captures a free-text reason string that the caller persists
// alongside the audit entry. Reason is required by default; pass
// `reasonRequired={false}` if a blank reason is acceptable.
//
// `extra` slot renders between the description and the textarea — used
// by the campaign cockpit's force-end modal to surface the
// `also_cancel_callbacks` toggle without forking the primitive.

import React, { useEffect, useRef, useState } from 'react';

import { Button, type ButtonIntent } from './Button';
import { Textarea } from './Textarea';

export interface ConfirmReasonModalProps {
  open: boolean;
  title: React.ReactNode;
  description?: React.ReactNode;
  /** Optional content rendered between the description and the reason
   * textarea. Use for action-specific extras like a destructive-action
   * toggle. */
  extra?: React.ReactNode;
  reasonLabel?: string;
  reasonPlaceholder?: string;
  reasonRequired?: boolean;
  confirmLabel?: string;
  cancelLabel?: string;
  confirmIntent?: ButtonIntent;
  saving?: boolean;
  /** Optional inline error rendered between extra and the textarea
   * (e.g. server 4xx body for the action). */
  error?: string | null;
  onCancel: () => void;
  onConfirm: (reason: string) => void;
}

export const ConfirmReasonModal: React.FC<ConfirmReasonModalProps> = ({
  open,
  title,
  description,
  extra,
  reasonLabel = 'Reason',
  reasonPlaceholder = 'Why this change?',
  reasonRequired = true,
  confirmLabel = 'Confirm',
  cancelLabel = 'Cancel',
  confirmIntent = 'primary',
  saving = false,
  error,
  onCancel,
  onConfirm,
}) => {
  const [reason, setReason] = useState('');
  const textareaRef = useRef<HTMLTextAreaElement | null>(null);

  useEffect(() => {
    if (open) {
      setReason('');
      // Defer focus to next tick so the modal mount completes first.
      window.setTimeout(() => textareaRef.current?.focus(), 0);
    }
  }, [open]);

  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && !saving) onCancel();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, saving, onCancel]);

  if (!open) return null;

  const canConfirm = reasonRequired ? reason.trim().length > 0 : true;
  const disabled = saving || !canConfirm;

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm p-4"
      onClick={() => !saving && onCancel()}
      role="dialog"
      aria-modal="true"
    >
      <div
        className="w-full max-w-md bg-surface-1 border border-white/[0.16] rounded-ds-xl p-5 shadow-ds-e3"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="text-base font-semibold text-slate-50 mb-1.5">{title}</div>
        {description ? (
          <div className="text-sm text-slate-400 mb-3.5 leading-relaxed">{description}</div>
        ) : null}
        {extra ? <div className="mb-3">{extra}</div> : null}
        {error ? (
          <div className="mb-3 px-3 py-2 rounded-ds-md border border-danger-border bg-danger-tint text-danger-text text-sm">
            {error}
          </div>
        ) : null}
        <Textarea
          ref={textareaRef}
          label={reasonLabel}
          value={reason}
          onChange={(e) => setReason(e.target.value)}
          placeholder={reasonPlaceholder}
          rows={3}
          disabled={saving}
        />
        <div className="flex justify-end gap-2 mt-3.5">
          <Button intent="ghost" size="sm" onClick={onCancel} disabled={saving}>
            {cancelLabel}
          </Button>
          <Button
            intent={confirmIntent}
            size="sm"
            onClick={() => onConfirm(reason.trim())}
            disabled={disabled}
          >
            {saving ? 'Saving…' : confirmLabel}
          </Button>
        </div>
      </div>
    </div>
  );
};

export default ConfirmReasonModal;
